/**
 * OnboardingRoute — for /onboarding.
 *
 * Only accessible to authenticated teachers who haven't finished
 * onboarding yet. Already-onboarded teachers go to the dashboard;
 * unauthenticated visitors go to /login.
 */
import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTeacher } from "../api/queries/teacher";
import LoadingScreen from "./LoadingScreen";

export default function OnboardingRoute({ children }: { children: ReactNode }) {
  const { data: teacher, isLoading } = useTeacher();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!teacher) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (teacher.onboardingComplete) {
    // Honor the returnTo that ProtectedRoute attached when it bounced a
    // not-yet-onboarded teacher here (e.g. /dashboard/learning-skills).
    // Previously this hardcoded /dashboard, silently dropping the intended
    // destination — that's what made the post-onboarding route-to-tool
    // land on the hub whenever a cache lag triggered the bounce. Validate
    // like the magic-link returnTo: an app-internal path, never an auth
    // screen (avoids a redirect loop back into /login or /onboarding).
    const raw = new URLSearchParams(location.search).get("returnTo");
    const dest =
      raw &&
      raw.startsWith("/") &&
      !raw.startsWith("//") &&
      !raw.startsWith("/onboarding") &&
      !raw.startsWith("/login")
        ? raw
        : "/dashboard";
    return <Navigate to={dest} replace />;
  }

  return <>{children}</>;
}
